import React from 'react';
import { useParams } from 'react-router-dom';
import type { TableSelectedState } from '../../../../../store';
import { StoreActions, StoreSelectors, useStoreDispatch, useStoreSelector } from '../../../../../store';
import {
  useOwnerGetEmployersQuery,
  useOwnerGetDetailQuery,
  useInsurerGetInsurersQuery,
  useDialog,
  useOwnerGetSubOwners,
} from '../../../../../hooks';
import { OWNER, SUBOWNER } from '../../../../../common/Authorization/entities';
import { EDIT, VIEW } from '../../../../../common/Authorization/permissions';
import { HasAccessTo } from '../../../../../components';

const SUB_OWNERS_DEFAULT_PAGE = 1;
const SUB_OWNERS_DEFAULT_PAGE_SIZE = 25;
const SUB_OWNERS_PAGE_SIZE_OPTIONS = [25, 50, 100, 250, 500, 1000];

const INSURERS_DEFAULT_PAGE = 1;
const INSURERS_DEFAULT_PAGE_SIZE = 25;

export const useOwnerInformationTab = () => {
  const dispatch = useStoreDispatch();
  const { ownerId } = useParams();
  const userId = useStoreSelector<string>(state => StoreSelectors.AppSelector.selectUserId(state.AppReducer));

  const [subOwnersPage, setSubOwnersPage] = React.useState(SUB_OWNERS_DEFAULT_PAGE);
  const [subOwnersPageSize, setSubOwnersPageSize] = React.useState(SUB_OWNERS_DEFAULT_PAGE_SIZE);

  const [insurersPage, setInsurersPage] = React.useState(INSURERS_DEFAULT_PAGE);
  const [insurersPageSize, setInsurersPageSize] = React.useState(INSURERS_DEFAULT_PAGE_SIZE);

  const onSubOwnersPaginationChange = React.useCallback(({ page, rowSize }: { page?: number; rowSize?: number }) => {
    if (typeof rowSize !== 'undefined') {
      setSubOwnersPageSize(rowSize);
      setSubOwnersPage(SUB_OWNERS_DEFAULT_PAGE);
    }
    if (typeof page !== 'undefined') {
      setSubOwnersPage(page);
    }
  }, []);

  const { page: subOwnerEmployersPage, pageSize: subOwnerEmployersPageSize } = useStoreSelector<TableSelectedState>(
    state => state.EmployersReducer,
  );

  const {
    dialogState: editOwnerDialogOpen,
    closeDialog: editOwnerCloseDialog,
    openDialog: editOwnerOpenDialog,
  } = useDialog();

  const {
    dialogState: viewSubOwnersDialogOpen,
    closeDialog: viewSubOwnersCloseDialog,
    openDialog: viewSubOwnersOpenDialog,
  } = useDialog();

  const {
    data: owner,
    isLoading: isOwnerLoading,
    refetch: refetchGetOwnerQuery,
  } = useOwnerGetDetailQuery({
    variables: { ownerId: ownerId || '' },
  });

  const { data: subOwners, isLoading: isLoadingSubOwners } = useOwnerGetSubOwners({
    variables: { userId, page: subOwnersPage, perPage: subOwnersPageSize },
    options: {
      enabled: viewSubOwnersDialogOpen,
      staleTime: Infinity,
    },
  });

  const isOwner = owner?.parent === null;
  const isSubOwner = !!owner?.parent;

  const { data: insurersQueryData, isLoading: insurersLoading } = useInsurerGetInsurersQuery({
    variables: { page: insurersPage, pageSize: insurersPageSize },
  });

  const insurers = insurersQueryData?.data ?? [];
  const page = insurersPage;
  const pageSize = insurersPageSize;
  const totalCount = insurersQueryData?.totalCount ?? 0;

  const { data: employersData, isLoading: isLoadingEmployers } = useOwnerGetEmployersQuery({
    variables: {
      userId: userId || '',
      page: subOwnerEmployersPage,
      perPage: subOwnerEmployersPageSize,
      ownerId: ownerId || '',
    },
    options: {
      enabled: isSubOwner,
      staleTime: Infinity,
    },
  });

  const disabledIfNoAccessToEdit = isOwner ? !HasAccessTo(OWNER, EDIT) : !HasAccessTo(SUBOWNER, EDIT);
  const disabledIfNoAccessToSubOwnerView = !HasAccessTo(SUBOWNER, VIEW);

  const disabledEditButtonIfLoadingOrNoOwnerOrNoAccess = isOwnerLoading || !owner?.ownerId || disabledIfNoAccessToEdit;
  const disabledShowSubOwnersButtonIfLoadingOrNoAccess = disabledIfNoAccessToSubOwnerView;

  const onClickEditButton = React.useCallback(() => {
    editOwnerOpenDialog();
  }, [editOwnerOpenDialog]);

  const onClickViewSubOwnersButton = React.useCallback(() => {
    viewSubOwnersOpenDialog();
  }, [viewSubOwnersOpenDialog]);

  const onEmployersTablePaginationChange = React.useCallback(
    ({ rowSize, page }: { rowSize?: number; page?: number }) => {
      if (typeof rowSize !== 'undefined') {
        dispatch(StoreActions.EmployersActions.setPageSize(rowSize, false));
      }
      if (typeof page !== 'undefined') {
        dispatch(StoreActions.EmployersActions.changePage(page, false));
      }
    },
    [dispatch],
  );

  const onInsurersTablePaginationChange = React.useCallback(
    ({ rowSize, page }: { rowSize?: number; page?: number }) => {
      if (typeof rowSize !== 'undefined') {
        setInsurersPageSize(rowSize);
        setInsurersPage(INSURERS_DEFAULT_PAGE);
      }
      if (typeof page !== 'undefined') {
        setInsurersPage(page);
      }
    },
    [],
  );

  return {
    owner,
    isOwnerLoading,
    refetchGetOwnerQuery,
    isOwner,
    isSubOwner,
    subOwners,
    isLoadingSubOwners,
    subOwnersPage,
    subOwnersPageSize,
    subOwnersPageSizeOptions: SUB_OWNERS_PAGE_SIZE_OPTIONS,
    subOwnersTotalCount: subOwners?.totalCount ?? 0,
    onSubOwnersPaginationChange,
    employersData,
    isLoadingEmployers,
    subOwnerEmployersPage,
    subOwnerEmployersPageSize,
    insurersLoading,
    insurers,
    page,
    pageSize,
    totalCount,
    editOwnerDialogOpen,
    editOwnerCloseDialog,
    editOwnerOpenDialog,
    viewSubOwnersDialogOpen,
    viewSubOwnersCloseDialog,
    disabledEditButtonIfLoadingOrNoOwnerOrNoAccess,
    disabledShowSubOwnersButtonIfLoadingOrNoAccess,
    onClickEditButton,
    onClickViewSubOwnersButton,
    onEmployersTablePaginationChange,
    onInsurersTablePaginationChange,
  };
};
